import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import reviewsApi from "@/api/reviewsApi";
import ReviewCard from "@/components/ReviewCard/ReviewCard";
import StarRating from "@/components/StarRating/StarRating";
import BookingResponse from "@/model/BookingResponse";
import ReviewPage from "@/model/ReviewPage";
import ReviewResponse from "@/model/ReviewResponse";
import "@/components/PropertyReviews/PropertyReviews.scss";

const PAGE_SIZE = 6;

interface PropertyReviewsProps {
  propertyId: string;
  /** A stay of the signed-in guest at this property that can be reviewed now, if any. */
  reviewableStay?: BookingResponse | null;
}

/** Rating summary (average + one bar per score) and the reviews, newest first, with "Show more". */
function PropertyReviews({ propertyId, reviewableStay }: PropertyReviewsProps) {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();

  const [summary, setSummary] = useState<ReviewPage | null>(null);
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(false);

    reviewsApi
      .listForProperty(propertyId, page, PAGE_SIZE, session?.accessToken)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setSummary(result);
        setReviews((current) =>
          page === 0
            ? (result.content ?? [])
            : [...current, ...(result.content ?? [])]
        );
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId, page, session]);

  const count = summary?.reviewCount ?? 0;
  const average = summary?.averageRating;
  const hasMore = summary ? page + 1 < (summary.totalPages ?? 0) : false;

  const reviewPrompt = reviewableStay ? (
    <Link
      className="property-reviews__cta"
      to={`/trips/${reviewableStay.id}/review`}
    >
      {t("reviews.leaveReview")}
    </Link>
  ) : !session ? (
    <p className="property-reviews__hint">
      {t("reviews.stayedHere")}{" "}
      <Link to="/signin" state={{ from: `/properties/${propertyId}` }}>
        {t("reviews.signInToReview")}
      </Link>
    </p>
  ) : null;

  return (
    <section
      className="property-reviews"
      id="reviews"
      aria-labelledby="reviews-title"
    >
      <div className="property-reviews__header">
        <h2 id="reviews-title">{t("reviews.title")}</h2>
        {reviewPrompt}
      </div>

      {error && <p role="alert">{t("reviews.loadError")}</p>}

      {!error && summary && count === 0 && (
        <p className="property-reviews__empty">{t("reviews.empty")}</p>
      )}

      {!error && summary && count > 0 && average !== undefined && (
        <div className="property-reviews__summary">
          <div className="property-reviews__average">
            <strong>
              {average.toLocaleString(i18n.language, {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              })}
            </strong>
            <StarRating value={average} size="medium" />
            <span>{t("reviews.count", { count })}</span>
          </div>
          <ul
            className="property-reviews__distribution"
            aria-label={t("reviews.distribution")}
          >
            {(summary.distribution ?? []).map((bucket) => (
              <li key={bucket.rating}>
                <span className="property-reviews__score">{bucket.rating}</span>
                <span className="property-reviews__bar" aria-hidden="true">
                  <span
                    style={{ width: `${((bucket.count ?? 0) / count) * 100}%` }}
                  />
                </span>
                <span className="property-reviews__bucket-count">
                  {t("reviews.bucket", {
                    count: bucket.count ?? 0,
                    rating: bucket.rating,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="property-reviews__list">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}

      {isLoading && (
        <p className="property-reviews__hint">{t("reviews.loading")}</p>
      )}

      {!isLoading && hasMore && (
        <button
          type="button"
          className="button-ghost property-reviews__more"
          onClick={() => setPage((current) => current + 1)}
        >
          {t("reviews.showMore")}
        </button>
      )}
    </section>
  );
}

export default PropertyReviews;
export type { PropertyReviewsProps };
